/**
 * REST client for the dashboard feedback surface — bug reports, product
 * bug reports, and feature requests, plus the attachment upload-URL mint.
 * Ported from origin/dashboard's api/client.js report methods, routed
 * through origin's `restPost`/`restGet` so bearer + 401-refresh match the
 * rest of the gateway calls.
 *
 *   POST /v1/attachments:upload-url   → mint a pre-signed PUT URL
 *   POST /v1/bug-reports              → endpoint-scoped bug report
 *   POST /v1/product-bug-reports      → product/dashboard bug report
 *   POST /v1/feature-requests         → feature request
 *   POST /v1/endpoints/metadata:get   → one endpoint's metadata snapshot
 *   GET  /v1/endpoints/metadata       → ABAC-scoped endpoint list (picker)
 *
 * Field names map proto camelCase ↔ snake_case at the boundary.
 */
import { restGet, restPost } from "./restClient.js";
import { flattenInventoryRow } from "./inventoryRows.js";

// Proto JSON enum values used by the REST gateway. Keep aligned with
// shared-protos/proto/support/v1/support.proto.
export const AttachmentPurpose = Object.freeze({
    BUG_REPORT:         "ATTACHMENT_PURPOSE_BUG_REPORT",
    FEATURE_REQUEST:    "ATTACHMENT_PURPOSE_FEATURE_REQUEST",
    PRODUCT_BUG_REPORT: "ATTACHMENT_PURPOSE_PRODUCT_BUG_REPORT",
});

export function createAttachmentUploadUrl({ purpose, filename, contentType, sizeBytes } = {}) {
    return restPost("/v1/attachments:upload-url", {
        purpose,
        filename,
        content_type: contentType,
        size_bytes: sizeBytes,
    });
}

export function createBugReport({
    title, description, affectedEndpointId, attachmentObjectKeys,
} = {}) {
    return restPost("/v1/bug-reports", {
        title,
        description,
        affected_endpoint_id: affectedEndpointId,
        attachment_object_keys: attachmentObjectKeys,
    });
}

export function createProductBugReport({ title, description, attachmentObjectKeys } = {}) {
    return restPost("/v1/product-bug-reports", {
        title,
        description,
        attachment_object_keys: attachmentObjectKeys,
    });
}

export function createFeatureRequest({ title, description, attachmentObjectKeys } = {}) {
    return restPost("/v1/feature-requests", {
        title,
        description,
        attachment_object_keys: attachmentObjectKeys,
    });
}

export function getEndpointMetadata(endpointId) {
    return restPost("/v1/endpoints/metadata:get", { endpoint_id: endpointId });
}

export async function listEndpoints({ page = 0, pageSize = 50, hostname } = {}) {
    const response = await restGet("/v1/endpoints/metadata", {
        page,
        page_size: pageSize,
        search: hostname || undefined,
    });
    const endpoints = (response?.metadata ?? []).map((info) => {
        const row = flattenInventoryRow(info);
        return {
            // Callers send this id back in proto `bytes` fields, so it stays
            // base64. `row.id` is the hyphenated display form.
            id: row.idRaw,
            hostname: row.hostname,
            serialNumber: row.serial,
            platform: row.platform,
            agentVersion: row.agentVersion,
            updatedAt: row.snapshotTime,
        };
    });
    return {
        endpoints,
        total: response?.total ?? endpoints.length,
        page: response?.page ?? page,
        pageSize: response?.pageSize ?? response?.page_size ?? pageSize,
    };
}
